import * as React from 'react';
import { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import dayjs from 'dayjs';
import TailwindModal from './Tailwind/TailwindModal';
import TailwindSelect from './Tailwind/TailwindSelect';
import TailwindButton from './Tailwind/TailwindButton';
import {
    getModelDataByScenario,
    compareScenariosFields,
} from '../shared/api/compare';
import snakeToPascal from '../tools';
import FieldsInitializer from './ModelDataFields/Input/Initializer';

import StringField from './ModelDataFields/Input/String';
import FloatField from './ModelDataFields/Input/Float';
import IntField from './ModelDataFields/Input/Int';
import DateField from './ModelDataFields/Input/Date';
import EnumField from './ModelDataFields/Input/Enum';
import BoolField from './ModelDataFields/Input/Bool';
import TimeSerieField from './ModelDataFields/Input/TimeSerie';
import TimeSerieTimingField from './ModelDataFields/Input/TimeSerieTiming';
import RelationField from './ModelDataFields/Input/Relation';
import ListField from './ModelDataFields/Input/List';
import CrossField from './ModelDataFields/Input/CrossField';
import TimingReferenceField from './ModelDataFields/Input/TimingReference';
import CustomizableTimingReferenceField from './ModelDataFields/Input/CustomizableTimingReference';

import OutputFloatField from './ModelDataFields/Output/Float';
import OutputGridField from './ModelDataFields/Output/Grid';
import DynamicGridField from './ModelDataFields/Output/DynamicGrid';
import OutputTimeSerieField from './ModelDataFields/Output/TimeSerie';
import { Box, Typography } from '@mui/material';

const FieldByKindAndType = {
    'Input': {
        'str': StringField,
        'int': IntField,
        'float': FloatField,
        'datetime': DateField,
        'enum': EnumField,
        'bool': BoolField,
        'list': ListField,
        'time_serie_timing': TimeSerieTimingField,
        'time_serie': TimeSerieField,
        'relation': RelationField,
        'timing_reference': TimingReferenceField,
        'customizable_timing_reference': CustomizableTimingReferenceField,
    },
    'Output': {
        'float': OutputFloatField,
        'time_serie': OutputTimeSerieField,
        'grid': OutputGridField,
        'dynamic_grid': DynamicGridField,
    }
}

class FieldErrorBoundary extends React.Component<
    { fallback: React.ReactNode; children: React.ReactNode },
    { hasError: boolean }
> {
    state = { hasError: false };
    static getDerivedStateFromError() { return { hasError: true }; }
    render() {
        return this.state.hasError ? this.fallback : this.props.children;
    }
    get fallback() { return this.props.fallback; }
}

export default function CompareModal({ open, setOpen, model, scenarios }) {

    const [scenarioA, setScenarioA] = useState('');
    const [scenarioB, setScenarioB] = useState('');
    const [outputModelDatas, setOutputModelDatas] = useState<any[]>([]);
    const [rows, setRows] = useState<{ modelDataId: string; fieldId: string }[]>([]);
    const [loading, setLoading] = useState(false);
    const [compareResult, setCompareResult] = useState<any>(null);

    type FieldLookupEntry = { field: any; modelDataName: string; fieldName: string };
    type ScenarioLookup = { input: Record<string, FieldLookupEntry>; output: Record<string, FieldLookupEntry> };

    const [lookupA, setLookupA] = useState<ScenarioLookup>({ input: {}, output: {} });
    const [lookupB, setLookupB] = useState<ScenarioLookup>({ input: {}, output: {} });

    const buildFieldLookup = (modelDatas: any[]) => {
        const lookup: Record<string, FieldLookupEntry> = {};
        for (const md of modelDatas) {
            if (!md.fields) continue;
            FieldsInitializer(md.fields);
            for (const [name, field] of Object.entries(md.fields) as [string, any][]) {
                if (field.hide) continue;
                if (field.id) {
                    lookup[field.id.toString()] = { field, modelDataName: md.name, fieldName: name };
                }
            }
        }
        return lookup;
    }

    const buildScenarioLookup = (data: any): ScenarioLookup => {
        const inputs = (data.input || []).filter((md: any) => md.parent_id !== null);
        const outputs = (data.output || []).filter((md: any) => md.parent_id !== null);
        return {
            input: buildFieldLookup(inputs),
            output: buildFieldLookup(outputs),
        };
    }

    useEffect(() => {
        if (scenarioA && scenarioB) {
            setCompareResult(null);

            Promise.all([
                getModelDataByScenario(Number(scenarioA)),
                getModelDataByScenario(Number(scenarioB)),
            ]).then(async ([responseA, responseB]) => {
                if (responseA) {
                    const dataA = await responseA.json();
                    const outputsA = (dataA.output || []).filter((md: any) => md.parent_id !== null);
                    setOutputModelDatas(outputsA);
                    setLookupA(buildScenarioLookup(dataA));
                }
                if (responseB) {
                    const dataB = await responseB.json();
                    setLookupB(buildScenarioLookup(dataB));
                }
            });
        } else {
            setOutputModelDatas([]);
            setLookupA({ input: {}, output: {} });
            setLookupB({ input: {}, output: {} });
            setCompareResult(null);
        }
    }, [scenarioA, scenarioB]);

    const handleClose = () => {
        setOpen(false);
    }

    const addRow = () => {
        setRows([...rows, { modelDataId: '', fieldId: '' }]);
        setCompareResult(null);
    }

    const removeRow = (index: number) => {
        setRows(rows.filter((_, i) => i !== index));
        setCompareResult(null);
    }

    const updateRow = (index: number, key: 'modelDataId' | 'fieldId', value: string) => {
        const newRows = [...rows];
        if (key === 'modelDataId') {
            newRows[index] = { modelDataId: value, fieldId: '' };
        } else {
            newRows[index] = { ...newRows[index], [key]: value };
        }
        setRows(newRows);
        setCompareResult(null);
    }

    const completedRows = rows.filter((r) => r.modelDataId && r.fieldId);

    const handleCompare = async () => {
        setLoading(true);
        try {
            const fieldIds = completedRows.map((r) => Number(r.fieldId));
            const response = await compareScenariosFields(Number(scenarioA), Number(scenarioB), fieldIds);
            if (response) {
                const data = await response.json();
                setCompareResult(data);
            }
        } finally {
            setLoading(false);
        }
    }

    const getFieldsForModelData = (modelDataId: string) => {
        const md = outputModelDatas.find((m) => m.id.toString() === modelDataId);
        if (!md || !md.fields) return [];
        return Object.entries(md.fields).filter(([_, field]: any) => !field.hide).map(([name, field]: [string, any]) => ({
            id: field.id.toString(),
            label: field.label || snakeToPascal(name),
        }));
    }

    const getScenarioName = (id: string) => {
        const s = scenarios.find((s) => s.id === id || s.id.toString() === id);
        return s ? s.name : '';
    }

    const getScenario = (id: string) => {
        return scenarios.find((s) => s.id === id || s.id.toString() === id);
    }

    const selectedScenarioA = getScenario(scenarioA);
    const selectedScenarioB = getScenario(scenarioB);
    const isScenarioANotComputed = selectedScenarioA && selectedScenarioA.is_computed === false;
    const isScenarioBNotComputed = selectedScenarioB && selectedScenarioB.is_computed === false;
    const hasScenariosNotComputed = isScenarioANotComputed || isScenarioBNotComputed;

    // Create refs for synchronized scrolling of time series
    const scrollRefsMap = React.useRef<Record<number, {
        refA: React.RefObject<HTMLDivElement>;
        refB: React.RefObject<HTMLDivElement>;
        isScrolling: { current: boolean };
    }>>({});

    const renderField = (fieldEntry: FieldLookupEntry | undefined, kind: string, compareWithEntry?: FieldLookupEntry, scrollRef?: React.RefObject<HTMLDivElement>, onScroll?: (e: React.UIEvent<HTMLDivElement>) => void) => {
        if (!fieldEntry) {
            return (
                <p className="text-sm text-slate-500 italic">
                    Field not found
                </p>
            );
        }
        const { field, fieldName } = fieldEntry;
        const componentKind = kind === 'input_field' ? 'Input' : 'Output';
        // For wide/complex field types, always use the Output (read-only) renderer
        const useOutputRenderer = ['time_serie', 'grid', 'dynamic_grid'].includes(field.type);
        const FieldComponent = useOutputRenderer
            ? FieldByKindAndType['Output']?.[field.type]
            : FieldByKindAndType[componentKind]?.[field.type];

        if (field.type === 'time_serie_timing' || field.type === 'customizable_timing_reference') {
            const raw = field.currentValue?.value ?? field.value?.value;
            const timing = raw?.timing ?? raw;
            if (timing) {
                const startRaw = timing.start_date?.value;
                const endRaw = timing.end_date?.value;
                const startDate = startRaw ? (dayjs.isDayjs(startRaw) ? startRaw : dayjs(startRaw)).format('DD/MM/YYYY') : '—';
                const endDate = endRaw ? (dayjs.isDayjs(endRaw) ? endRaw : dayjs(endRaw)).format('DD/MM/YYYY') : '—';
                const periodicity = timing.periodicity?.value && timing.periodicity.value !== 'null'
                    ? snakeToPascal(timing.periodicity.value)
                    : '—';
                return (
                    <Box sx={{ marginLeft: 1 }}>
                        <Typography variant="body2"><b>Start Date:</b> {startDate}</Typography>
                        <Typography variant="body2"><b>End Date:</b> {endDate}</Typography>
                        <Typography variant="body2"><b>Periodicity:</b> {periodicity}</Typography>
                    </Box>
                );
            }
        }

        const rawFallback = () => {
            const raw = field.currentValue?.value ?? field.value?.value;
            const display = (raw === null || raw === undefined)
                ? '—'
                : (typeof raw === 'object' ? JSON.stringify(raw) : String(raw));
            return <p className="text-sm">{display}</p>;
        };

        if (FieldComponent) {
            return (
                <FieldErrorBoundary fallback={rawFallback()}>
                    <FieldComponent
                        name={snakeToPascal(fieldName)}
                        field={field}
                        siblingFields={{}}
                        reloadState={() => {}}
                        currentState={{}}
                        recreateExcelInput={() => {}}
                        compareWithField={compareWithEntry?.field}
                        scrollRef={scrollRef}
                        onScroll={onScroll}
                    />
                </FieldErrorBoundary>
            );
        }
        return rawFallback();
    }

    // Build scenario options
    const scenarioAOptions = scenarios
        .filter((s) => s.id !== scenarioB)
        .map((s) => ({ value: s.id, label: s.name }));

    const scenarioBOptions = scenarios
        .filter((s) => s.id !== scenarioA)
        .map((s) => ({ value: s.id, label: s.name }));

    return (
        <TailwindModal
            isOpen={open}
            onClose={handleClose}
            title={`${model.name} / Compare`}
            size="full"
            showCloseButton={true}
        >
            <div className="flex flex-col gap-4">
                <p className="text-slate-700">
                    In this section, you can compare scenarios of your financial model.
                </p>

                {/* Scenario Selectors */}
                <div className="flex flex-row gap-6">
                    <div className="flex-1">
                        <TailwindSelect
                            label="Scenario A"
                            value={scenarioA}
                            onChange={(value) => setScenarioA(value)}
                            options={scenarioAOptions}
                            placeholder="Select a scenario"
                        />
                    </div>
                    <div className="flex-1">
                        <TailwindSelect
                            label="Scenario B"
                            value={scenarioB}
                            onChange={(value) => setScenarioB(value)}
                            options={scenarioBOptions}
                            placeholder="Select a scenario"
                        />
                    </div>
                </div>

                {hasScenariosNotComputed && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded">
                        <div className="flex items-center mb-2">
                            <span className="material-symbols-outlined text-red-600 text-xl mr-2">warning</span>
                            <p className="text-sm font-bold text-red-600">
                                Unable to compare scenarios
                            </p>
                        </div>
                        <p className="text-sm ml-8 text-red-600">
                            {isScenarioANotComputed && isScenarioBNotComputed
                                ? `Both scenarios "${selectedScenarioA?.name}" and "${selectedScenarioB?.name}" have not been computed yet.`
                                : isScenarioANotComputed
                                ? `Scenario "${selectedScenarioA?.name}" has not been computed yet.`
                                : `Scenario "${selectedScenarioB?.name}" has not been computed yet.`}
                        </p>
                        <p className="text-sm ml-8 mt-2 text-red-600">
                            Please complete the scenario inputs and wait for the calculation to finish before comparing scenarios.
                        </p>
                    </div>
                )}

                {scenarioA && scenarioB && !hasScenariosNotComputed && outputModelDatas.length > 0 && (
                    <div className="mt-6">
                        <p className="text-sm font-bold mb-4">
                            Fields to compare
                        </p>
                        {rows.map((row, index) => {
                            const modelDataOptions = outputModelDatas.map(md => ({
                                value: md.id.toString(),
                                label: md.name
                            }));
                            const fieldOptions = getFieldsForModelData(row.modelDataId).map(f => ({
                                value: f.id,
                                label: f.label
                            }));

                            return (
                                <div key={index} className="flex flex-row items-center gap-4 mb-3">
                                    <div className="flex-1">
                                        <TailwindSelect
                                            value={row.modelDataId}
                                            onChange={(value) => updateRow(index, 'modelDataId', value)}
                                            options={[
                                                { value: '', label: 'Select a model data', disabled: true },
                                                ...modelDataOptions
                                            ]}
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <TailwindSelect
                                            value={row.fieldId}
                                            onChange={(value) => updateRow(index, 'fieldId', value)}
                                            options={[
                                                { value: '', label: 'Select a field', disabled: true },
                                                ...fieldOptions
                                            ]}
                                            disabled={!row.modelDataId}
                                        />
                                    </div>
                                    <button
                                        onClick={() => removeRow(index)}
                                        className="p-1 hover:bg-slate-100 rounded transition-colors"
                                        title="Remove field"
                                    >
                                        <span className="material-symbols-outlined text-slate-600 text-xl">delete_forever</span>
                                    </button>
                                </div>
                            );
                        })}
                        <div className="flex items-center justify-between mt-2">
                            <button
                                onClick={addRow}
                                className="flex items-center gap-1 text-sm hover:bg-slate-100 p-1 rounded transition-colors"
                            >
                                <span className="material-symbols-outlined text-slate-600 text-xl">add</span>
                                <span className="text-sm">Add a field</span>
                            </button>
                            {completedRows.length > 0 && (
                                <TailwindButton
                                    onClick={handleCompare}
                                    disabled={loading}
                                    variant="primary"
                                >
                                    {loading && (
                                        <div className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                    )}
                                    {!loading && (
                                        <span className="material-symbols-outlined text-lg mr-1">compare_arrows</span>
                                    )}
                                    {loading ? 'Comparing...' : 'Compare'}
                                </TailwindButton>
                            )}
                        </div>
                    </div>
                )}

                {compareResult && !hasScenariosNotComputed && (
                    <div className="mt-6">
                        <hr className="border-t border-slate-200 mb-6" />

                        {/* Missing model datas */}
                        {compareResult.missing_model_datas && (
                            (compareResult.missing_model_datas.in_tree_1?.length > 0 || compareResult.missing_model_datas.in_tree_2?.length > 0) && (
                            <div className="mb-6">
                                <p className="text-base font-bold mb-4">
                                    Missing Model Datas
                                </p>

                                {compareResult.missing_model_datas.in_tree_1?.length > 0 && (
                                    <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded">
                                        <div className="flex items-center mb-2">
                                            <span className="material-symbols-outlined text-orange-600 text-xl mr-2">warning</span>
                                            <p className="text-sm font-bold text-orange-600">
                                                Only in {getScenarioName(scenarioA)} (not in {getScenarioName(scenarioB)})
                                            </p>
                                        </div>
                                        {compareResult.missing_model_datas.in_tree_1.map((md: any) => (
                                            <p key={md.id} className="text-sm ml-8 text-orange-600">
                                                • {md.name} ({md.kind})
                                            </p>
                                        ))}
                                    </div>
                                )}

                                {compareResult.missing_model_datas.in_tree_2?.length > 0 && (
                                    <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded">
                                        <div className="flex items-center mb-2">
                                            <span className="material-symbols-outlined text-orange-600 text-xl mr-2">warning</span>
                                            <p className="text-sm font-bold text-orange-600">
                                                Only in {getScenarioName(scenarioB)} (not in {getScenarioName(scenarioA)})
                                            </p>
                                        </div>
                                        {compareResult.missing_model_datas.in_tree_2.map((md: any) => (
                                            <p key={md.id} className="text-sm ml-8 text-orange-600">
                                                • {md.name} ({md.kind})
                                            </p>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}

                        {/* Similar fields (those compared but without differences) */}
                        {(() => {
                            const comparedFieldIds = completedRows.map((r) => Number(r.fieldId));
                            const differenceFieldIds = (compareResult.differences || []).map((diff: any) => diff.id_1 || diff.id_2);
                            const similarFieldIds = comparedFieldIds.filter((id) => !differenceFieldIds.includes(id));

                            if (similarFieldIds.length > 0) {
                                return (
                                    <div className="mb-6">
                                        <p className="text-base font-bold mb-4">
                                            Similar Fields
                                        </p>
                                        <div className="p-4 bg-green-50 border border-green-200 rounded">
                                            <p className="text-sm mb-2 text-green-600">
                                                The following fields are identical in both scenarios:
                                            </p>
                                            {similarFieldIds.map((fieldId) => {
                                                // Try to find the field in output lookups
                                                const entryA = lookupA.output[fieldId.toString()];
                                                const entryB = lookupB.output[fieldId.toString()];
                                                const entry = entryA || entryB;

                                                if (entry) {
                                                    const fieldDisplayName = `${entry.modelDataName} — ${snakeToPascal(entry.fieldName)}`;
                                                    return (
                                                        <p key={fieldId} className="text-sm ml-8 text-green-700">
                                                            • {fieldDisplayName}
                                                        </p>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        })()}

                        {/* Field differences */}
                        {compareResult.differences?.length > 0 && (
                            <div>
                                <p className="text-base font-bold mb-4">
                                    Field Differences
                                </p>
                                {compareResult.differences.map((diff: any, index: number) => {
                                    const lookupKey = diff.kind === 'input_field' ? 'input' : 'output';
                                    const entryA = lookupA[lookupKey][diff.id_1?.toString()];
                                    const entryB = lookupB[lookupKey][diff.id_2?.toString()];
                                    const fieldDisplayName = entryA
                                        ? `${entryA.modelDataName} — ${snakeToPascal(entryA.fieldName)}`
                                        : entryB
                                            ? `${entryB.modelDataName} — ${snakeToPascal(entryB.fieldName)}`
                                            : `Field #${diff.id_1} / #${diff.id_2}`;

                                    const isWideField = entryA?.field?.type === 'time_serie' || entryB?.field?.type === 'time_serie'
                                        || entryA?.field?.type === 'grid' || entryB?.field?.type === 'grid'
                                        || entryA?.field?.type === 'dynamic_grid' || entryB?.field?.type === 'dynamic_grid';

                                    const isTimeSerie = entryA?.field?.type === 'time_serie' || entryB?.field?.type === 'time_serie';

                                    // Get or create refs for this difference
                                    if (!scrollRefsMap.current[index]) {
                                        scrollRefsMap.current[index] = {
                                            refA: React.createRef<HTMLDivElement>(),
                                            refB: React.createRef<HTMLDivElement>(),
                                            isScrolling: { current: false }
                                        };
                                    }

                                    const { refA: scrollRefA, refB: scrollRefB, isScrolling: isScrollingRef } = scrollRefsMap.current[index];

                                    const handleScrollA = (e: React.UIEvent<HTMLDivElement>) => {
                                        if (!isScrollingRef.current && scrollRefB.current) {
                                            isScrollingRef.current = true;
                                            scrollRefB.current.scrollLeft = (e.target as HTMLDivElement).scrollLeft;
                                            requestAnimationFrame(() => {
                                                isScrollingRef.current = false;
                                            });
                                        }
                                    };

                                    const handleScrollB = (e: React.UIEvent<HTMLDivElement>) => {
                                        if (!isScrollingRef.current && scrollRefA.current) {
                                            isScrollingRef.current = true;
                                            scrollRefA.current.scrollLeft = (e.target as HTMLDivElement).scrollLeft;
                                            requestAnimationFrame(() => {
                                                isScrollingRef.current = false;
                                            });
                                        }
                                    };

                                    return (
                                        <div key={index} className="mb-6 p-4 border border-slate-200 rounded">
                                            <div className="flex items-center gap-2 mb-4">
                                                <p className="text-sm font-bold">
                                                    {fieldDisplayName}
                                                </p>
                                                <span className={`text-xs font-bold px-2 py-1 rounded ${
                                                    diff.kind === 'input_field'
                                                        ? 'bg-blue-100 text-blue-700'
                                                        : 'bg-purple-100 text-purple-700'
                                                }`}>
                                                    {diff.kind === 'input_field' ? 'Input' : 'Output'}
                                                </span>
                                            </div>
                                            <div className={`flex ${isWideField ? 'flex-col gap-4' : 'flex-row gap-8'}`}>
                                                <div className={`${isWideField ? '' : 'flex-1'} overflow-hidden`}>
                                                    <p className="text-xs font-bold text-slate-600 mb-2 block">
                                                        {getScenarioName(scenarioA)}
                                                    </p>
                                                    {renderField(entryA, diff.kind, entryB, isTimeSerie ? scrollRefA : undefined, isTimeSerie ? handleScrollA : undefined)}
                                                </div>
                                                <div className={`${isWideField ? '' : 'flex-1'} overflow-hidden`}>
                                                    <p className="text-xs font-bold text-slate-600 mb-2 block">
                                                        {getScenarioName(scenarioB)}
                                                    </p>
                                                    {renderField(entryB, diff.kind, entryA, isTimeSerie ? scrollRefB : undefined, isTimeSerie ? handleScrollB : undefined)}
                                                </div>
                                            </div>
                                            {diff.dif && (
                                                <div className="mt-4 p-3 bg-slate-50 rounded">
                                                    <p className="text-sm text-slate-700">
                                                        {diff.dif}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {compareResult.differences?.length === 0 &&
                            compareResult.missing_model_datas?.in_tree_1?.length === 0 &&
                            compareResult.missing_model_datas?.in_tree_2?.length === 0 && (
                            <p className="text-base text-center text-slate-600">
                                No differences found between the two scenarios.
                            </p>
                        )}
                    </div>
                )}
            </div>
        </TailwindModal>
    )
}
